import type { Signal } from '@angular/core'
import { Injectable, computed, inject } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { Order } from '@awork/core/state/signal-store/types'
import type { Team } from '@awork/features/team/models/team.model'
import { TeamStore } from '@awork/features/team/state/team.store'
import { UserQuery } from '@awork/features/user/state/user.query'

@Injectable({ providedIn: 'root' })
export class TeamQuery extends EntitySignalQuery<Team> {
  protected store: TeamStore
  private userQuery = inject(UserQuery)

  constructor() {
    const store = inject(TeamStore)

    super(store)

    this.store = store
  }

  /**
   * Gets team by id
   * @param {string} id
   * @returns {Team}
   */
  getTeam(id: string): Team {
    const team = this.getEntity(id)

    if (!team?.id) {
      return null
    }

    this.setUsers(team)

    return team
  }

  /**
   * Query team by id using Signal
   * @param {string} id
   * @returns {Signal<Team>}
   */
  queryTeam(id: string): Signal<Team> {
    const team = this.queryEntity(id)

    return computed(() => {
      const teamValue = team()

      if (!teamValue) {
        return null
      }

      this.setUsers(teamValue)

      return teamValue
    })
  }

  /**
   * Query all teams using Signals
   * @param {{ limit?: number; searchQuery?: string; excludedIds?: string[]; }} [args={ limit: null, searchQuery: null, excludedIds: [] }] The arguments for querying teams.
   * @returns {Signal<Team[]>} A signal containing the list of teams.
   * @param {number} [args.limit] - The maximum number of teams to return.
   * @param {string} [args.searchQuery] - The search query to filter teams.
   * @param {string[]} [args.excludedIds] - The IDs of teams to exclude from the results.
   */
  queryAllTeams(
    args: { limit?: number; searchQuery?: string; excludedIds?: string[] } = {
      limit: null,
      searchQuery: null,
      excludedIds: []
    }
  ): Signal<Team[]> {
    const { excludedIds, searchQuery, limit } = args
    const teams = this.queryAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: team => {
        const isNotExcluded = !excludedIds?.includes(team.id)
        const matchesQuery = !searchQuery || team.name?.toLowerCase().includes(searchQuery.toLowerCase())

        return isNotExcluded && matchesQuery
      },
      limitTo: limit
    })

    return computed(() => {
      return (
        this.mapEntities(
          teams().map(team => {
            this.setUsers(team)
            return team
          })
        ) || []
      )
    })
  }

  /**
   * Gets teams by id
   * @param {string[]} ids
   * @returns {Team[]}
   */
  getTeamsById(ids: string[]): Team[] {
    return this.getAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: team => ids.includes(team.id)
    }).map(team => {
      this.setUsers(team)
      return team
    })
  }

  /**
   * Queries teams by id using Signals
   * @param {string[]} ids
   * @returns {Signal<Team[]>}
   */
  queryTeamsById(ids: string[]): Signal<Team[]> {
    const teams = this.queryAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: team => ids.includes(team.id)
    })

    return computed(() => {
      teams().map(team => {
        this.setUsers(team)
      })
      return teams()
    })
  }

  /**
   * Get all teams
   * @param {number} limit
   * @param {string} searchQuery
   * @returns {Team[]}
   */
  getAllTeams(limit?: number, searchQuery?: string): Team[] {
    return this.getAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: searchQuery ? team => team.name.includes(searchQuery) : undefined,
      limitTo: limit
    }).map(team => {
      this.setUsers(team)
      return team
    })
  }

  /**
   * Gets the teams where the user belongs
   * @param {string} userId
   * @returns {Team[]}
   */
  getUserTeams(userId: string): Team[] {
    return this.getAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: team => team.userIds?.includes(userId)
    }).map(team => {
      this.setUsers(team)
      return this.mapEntity(team)
    })
  }

  /**
   * Query the teams where the user belongs using Signals
   * @param {string} userId
   * @returns {Signal<Team[]>}
   */
  queryUserTeams(userId: string): Signal<Team[]> {
    const teams = this.queryAll({
      sortBy: 'name',
      sortByOrder: Order.ASC,
      filterBy: team => team.userIds?.includes(userId)
    })

    return computed(() => {
      return this.mapEntities(
        teams().map(team => {
          this.setUsers(team)
          return team
        })
      )
    })
  }

  /**
   * Sets the team's users using the user store
   * @param {Team} team
   */
  setUsers(team: Team): void {
    if (team.userIds) {
      team.users = this.userQuery.getUsersByIds(team.userIds)
    }
  }
}
